// 시험 미리보기(시험 목록 ⋮ → 미리보기, ExamList.openPreview → ExamDetailModal)가 버전(A형·B형)마다
// 스냅샷(GET /exams/{exam_id}/questions-full?version=)을 그 버전의 순서로 그룹 블록으로 묶는 순수 함수.
// 규칙은 docs/시험_관리_API.md 16절 「강사 화면 시험 미리보기가 읽는 필드」.
// - 버전을 섞지 않는다. 2026-10-15 전에는 A·B 응답을 한 배열로 합쳐 그룹 보기에 같은 문항이 두 번 그려지고 B형의 순서는 보이지 않았다.
// - B형은 A형과 같은 문항·배점을 문항 단위로 섞은 것이라 한 그룹의 문항이 떨어져 놓인다(2026-10-15 성취도 백엔드 회신).
//   그래서 그룹 블록은 그 버전의 순서에서 같은 그룹이 이어지는 구간마다 하나씩 만들고, 문항 번호는 버전 안에서 1부터 이어 센다.
import { hasHtmlContent } from './html.js'
import { normalizeGroupVideos } from './groupVideos.js'

// 버전 키 표시 순서. 성취도 백엔드는 A 를 기본으로 두고 settings.generateABVersion 이고 문항이 2개 이상이면 B 를 더 만든다
const VERSION_ORDER = ['A', 'B']

export const versionLabel = (key) => `${key}형`

// 시험이 가진 버전 키(A·B 순, 그 밖의 키는 뒤에 이름순). versions 가 없거나 비면 ['A'].
// B 가 없는 시험은 B 키 자체가 없다(빈 객체 아님) — 목록·조회 응답 모두 저장된 versions 그대로
export const getExamVersionKeys = (exam) => {
  const versions = exam && exam.versions && typeof exam.versions === 'object' ? exam.versions : null
  const keys = versions ? Object.keys(versions).filter(key => versions[key]) : []
  if (keys.length === 0) return ['A']
  const known = VERSION_ORDER.filter(key => keys.includes(key))
  const others = keys.filter(key => !VERSION_ORDER.includes(key)).sort()
  return [...known, ...others]
}

const questionIdOf = (q) => (q && (q.questionId || q.id || q._id)) || ''

// skill 은 소문자로. 비어 있거나 unknown 이면 questionType 을 본다
const normalizeSkill = (q) => {
  let skill = q.skill || q.category || q.type || ''
  if (typeof skill === 'string') skill = skill.toLowerCase()
  if (!skill || skill === 'unknown') {
    skill = q.questionType || q.question_type || 'unknown'
    if (typeof skill === 'string') skill = skill.toLowerCase()
  }
  return skill
}

// choices 는 배열 또는 객체 하나로 온다. 그림 선택지는 image.file 만 있다(docs/문제은행_시스템.md 「3. Choice」)
const mapChoices = (choices) => {
  let list = []
  if (Array.isArray(choices)) list = choices
  else if (choices && typeof choices === 'object') list = [choices]
  return list.map(c => ({
    choiceText: c.choiceText || c.text || c.option_text || '',
    isAnswer: !!(c.isAnswer || c.correct || c.is_answer),
    image: c.image || null
  }))
}

// 스냅샷 문항 하나를 미리보기가 읽는 모양으로
export const mapPreviewQuestion = (q) => ({
  questionId: questionIdOf(q),
  skill: normalizeSkill(q),
  level: q.level || '',
  questionText: q.questionText || q.content || q.question_text || '',
  content: q.content || q.questionText || q.question_content || '',
  score: q.score || q.points || 0,
  questionAudio: q.questionAudio || null,
  choices: mapChoices(q.choices || q.options)
})

// 문항 중 하나라도 questionGroupId 와 함께 지시사항·보기·읽기 지문·그룹 오디오·그룹 동영상(비어 있지 않은 배열) 중 하나를 가지면 그룹 보기
export const hasGroupInfo = (items) => (Array.isArray(items) ? items : []).some(item =>
  item && item.questionGroupId && (
    item.instruction || item.example || item.readingScript ||
    (item.groupAudio && (item.groupAudio.file || item.groupAudio.script)) ||
    normalizeGroupVideos(item.groupVideos).length > 0
  ))

// 한 버전의 스냅샷을 그 버전의 순서로. questionIds 순서를 먼저 따르고, 거기 없는 문항은 응답 순서대로 뒤에 둔다.
// (스냅샷과 questionIds 는 같은 목록에서 함께 만들어 순서가 같다 — 2026-10-15 성취도 백엔드 회신.) 같은 ID 가 두 번 오면 처음 것만 쓴다.
export const orderVersionItems = (items, questionIds) => {
  const byId = new Map()
  ;(Array.isArray(items) ? items : []).forEach(q => {
    const id = questionIdOf(q)
    if (id && !byId.has(id)) byId.set(id, q)
  })
  const ordered = []
  const used = new Set()
  ;(Array.isArray(questionIds) ? questionIds : []).forEach(id => {
    if (byId.has(id) && !used.has(id)) {
      used.add(id)
      ordered.push(byId.get(id))
    }
  })
  byId.forEach((q, id) => {
    if (!used.has(id)) {
      used.add(id)
      ordered.push(q)
    }
  })
  return ordered
}

// 한 버전을 { key, label, questions, questionGroups } 로.
// - questions: 그 버전의 순서, number 는 1부터(버전 안에서 이어 센다 — 채점 대시보드의 문항 번호와 같은 기준).
// - questionGroups: 같은 그룹이 이어지는 구간마다 블록 하나. B형처럼 한 그룹의 문항이 떨어져 있으면 그 그룹의 블록이 여러 개 생기므로
//   blockId 로 구분한다. 그룹 필드는 문항마다 펼쳐 오므로 블록의 첫 문항 값을 쓴다.
// - 그룹 필드가 하나도 없는 예전 스냅샷은 questionGroups 가 [] 이고 모달이 questions 로 문항 목록 보기를 그린다.
export const buildPreviewVersion = ({ key, items, questionIds }) => {
  const ordered = orderVersionItems(items, questionIds)
  const questions = ordered.map((q, i) => ({ ...mapPreviewQuestion(q), number: i + 1 }))
  const questionGroups = []
  if (hasGroupInfo(ordered)) {
    let block = null
    ordered.forEach((q, i) => {
      const groupId = q.questionGroupId || 'ungrouped'
      if (!block || block.groupId !== groupId) {
        block = {
          blockId: `${groupId}#${i + 1}`,
          groupId,
          skill: q.skill || q.category || '',
          textBook: q.textBook || '',
          instruction: q.instruction || '',
          example: q.example || '',
          readingScript: hasHtmlContent(q.readingScript) ? q.readingScript : '',
          groupAudio: q.groupAudio || null,
          // 그룹 동영상 — 2026-10-12 부터 스냅샷이 문항마다 담는다(항상 배열). 그 전 시험은 키가 없어 [] 로
          groupVideos: normalizeGroupVideos(q.groupVideos),
          questions: []
        }
        questionGroups.push(block)
      }
      block.questions.push(questions[i])
    })
  }
  return { key, label: versionLabel(key), questions, questionGroups }
}
